/**
 * admin/TermsOfUse.tsx — PlatformOwner-only publishing of the Terms of Use (TU-F10), reskinned to the
 * teal "upload" design system and mounted as an in-place modal panel over AdminHome (TermsOfUsePanel)
 * rather than the old full-page navy screen at /admin/terms. The owner edits the policy text and
 * publishes a NEW version, with no deploy. Prior versions are kept and listed; nothing here overwrites
 * or deletes one (TU-NFR05) — publishing is always additive.
 *
 * A `material` change re-gates every returning employee on their next arrival (TU-F11) and its change
 * summary is shown at the top of the re-gated policy (TU-F12); a `minor` change updates the footer
 * document without re-gating. The form makes that choice explicit so the owner is never surprised by
 * whether they just re-gated the whole workforce.
 *
 * The panel fetches its own data on open via useOverlayData (AdminHome does not preload it) and, after
 * a publish, calls reload() to refresh the version list in place instead of a router revalidation. The
 * still-registered /admin/terms route renders through a thin TermsOfUseAdmin() wrapper that opens the
 * same panel over a blank backdrop for the deep-link path.
 *
 * Provisional engineering UI — matches the other admin governance panels' own posture.
 */
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  apiGet,
  apiPost,
  type PolicyVersionRow,
  type PublishVersionBody,
  type TermsChangeKind,
} from './api';
import {
  EmptyState,
  ErrorLine,
  KombiAdminOverlay,
  NotPermitted,
  Notice,
  PrimaryAction,
  ProvisionalNote,
  SectionHeader,
  Spinner,
} from '../kombi/admin/chrome';
import { useOverlayData, type ReturnFocusRef } from './overlay';

export type TermsAdminLoaderData =
  { state: 'signedOut' } | { state: 'forbidden' } | { state: 'ok'; versions: PolicyVersionRow[] };

export async function termsAdminLoader(): Promise<TermsAdminLoaderData> {
  const res = await apiGet<{ versions: PolicyVersionRow[] }>('/api/admin/terms');
  if (res.kind === 'unauthorized') return { state: 'signedOut' };
  if (res.kind === 'error') {
    if (res.status === 403) return { state: 'forbidden' };
    throw new Error(`GET /api/admin/terms failed (${res.status})`);
  }
  return { state: 'ok', versions: res.body.versions };
}

/** Human date for the list; the value is already YYYY-MM-DD (effective) or ISO (published). */
function fmtDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** The publish form. All state local; on success it reloads the panel's data so the list refreshes. */
function PublishForm({ onPublished }: { onPublished: () => void }) {
  const [versionLabel, setVersionLabel] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [changeKind, setChangeKind] = useState<TermsChangeKind>('material');
  const [changeSummary, setChangeSummary] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setVersionLabel('');
    setEffectiveDate('');
    setChangeKind('material');
    setChangeSummary('');
    setBody('');
  }

  async function publish() {
    setError(null);
    setMessage(null);

    // A friendly first pass; the server is the real validator either way.
    if (!versionLabel.trim()) return setError('A version label is required (e.g. “v1.1”).');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(effectiveDate))
      return setError('An effective date is required, as YYYY-MM-DD.');
    if (!body.trim()) return setError('The policy text cannot be empty.');

    setBusy(true);
    try {
      const payload: PublishVersionBody = {
        versionLabel: versionLabel.trim(),
        body: body.trim(),
        effectiveDate: effectiveDate.trim(),
        changeKind,
        ...(changeSummary.trim() ? { changeSummary: changeSummary.trim() } : {}),
      };
      const res = await apiPost<{ versionId: string }>('/api/admin/terms', payload);
      if (res.kind === 'ok') {
        setMessage(
          changeKind === 'material'
            ? 'Published. Returning employees will be asked to accept this version on their next visit.'
            : 'Published as a minor change. The footer document is updated; nobody is re-gated.',
        );
        reset();
        onPublished();
        return;
      }
      if (res.kind === 'error' && res.status === 409) {
        setError('That version label is already in use. Choose a different label.');
        return;
      }
      if (res.kind === 'error' && res.status === 400) {
        setError('The server rejected the form. Check the label, date and policy text.');
        return;
      }
      setError(`Publishing failed (${res.kind === 'error' ? res.status : 'not signed in'}).`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="kombi-admin-card"
      onSubmit={(e) => {
        e.preventDefault();
        void publish();
      }}
    >
      <Notice>
        Publishing adds a new version — it never overwrites or deletes an existing one. A{' '}
        <strong>material</strong> change re-gates every returning employee; a <strong>minor</strong>{' '}
        change (a typo or clarification) updates the footer document without re-gating anyone.
      </Notice>

      <div className="kombi-dialog-stack">
        {/* Native inputs, so the browser's own date picker and validation apply. FormField took an
            onChange of (value: string); a native one takes the event, hence the unwrapping here. */}
        <label className="kombi-field">
          <span>Version label</span>
          <input
            id="tou-label"
            onChange={(event) => setVersionLabel(event.target.value)}
            placeholder="v1.1"
            type="text"
            value={versionLabel}
          />
        </label>

        <label className="kombi-field">
          <span>Effective date</span>
          <input
            id="tou-effective-date"
            onChange={(event) => setEffectiveDate(event.target.value)}
            type="date"
            value={effectiveDate}
          />
        </label>

        <label className="kombi-field">
          <span>Change kind</span>
          <select
            value={changeKind}
            onChange={(e) => setChangeKind(e.target.value as TermsChangeKind)}
          >
            <option value="material">Material — re-gate everyone (TU-F11)</option>
            <option value="minor">Minor — footer update only, no re-gate</option>
          </select>
        </label>

        <label className="kombi-field">
          <span>
            What changed {changeKind === 'minor' ? '(optional)' : '(shown to re-gated employees)'}
          </span>
          <textarea
            value={changeSummary}
            onChange={(e) => setChangeSummary(e.target.value)}
            placeholder="Plain-words summary of what changed since the last version."
            maxLength={2000}
          />
        </label>

        <label className="kombi-field">
          <span>Policy text</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="The full policy. Plain text; # / ## headings and **bold** are rendered in the gate."
          />
        </label>
      </div>

      <div className="kombi-admin-row-actions">
        <PrimaryAction submit>{busy ? 'Publishing…' : 'Publish version'}</PrimaryAction>
        {message && <span className="learn-note">{message}</span>}
      </div>

      {error && <ErrorLine>{error}</ErrorLine>}
    </form>
  );
}

/**
 * The overlay body AdminHome mounts. Fetches its own data on open (useOverlayData → termsAdminLoader),
 * shows the pending line while loading, an ErrorLine for a failed load / ended session, NotPermitted
 * for a forbidden result, and the publish form + version list on success — a publish reload()s in place.
 */
export function TermsOfUsePanel({
  onClose,
  returnFocusRef: _returnFocusRef,
}: {
  onClose: () => void;
  returnFocusRef: ReturnFocusRef;
}) {
  const { data, loading, error, reload } = useOverlayData(termsAdminLoader);

  return (
    <KombiAdminOverlay title="Terms of Use" onClose={onClose}>
      {loading ? (
        <Spinner label="Loading terms…" />
      ) : error || (data && data.state === 'signedOut') ? (
        // signedOut inside an overlay: don't navigate — the parent grid owns auth. Just say so.
        <ErrorLine>
          Your session has ended or the terms could not be loaded. Close this panel and sign in
          again.
        </ErrorLine>
      ) : data && data.state === 'forbidden' ? (
        <NotPermitted what="Terms of Use" />
      ) : data && data.state === 'ok' ? (
        <div>
          <ProvisionalNote />
          <Notice>
            Edit and publish the platform&rsquo;s usage policy with no deploy (TU-F10). Every
            published version is kept so a past decision that cited it can still be understood
            (TU-NFR05).
          </Notice>

          <SectionHeader>Publish a version</SectionHeader>
          <PublishForm onPublished={reload} />

          <SectionHeader>Published versions</SectionHeader>
          {data.versions.length === 0 ? (
            <EmptyState>No versions yet.</EmptyState>
          ) : (
            <ul className="kombi-dialog-stack">
              {data.versions.map((v) => (
                <li key={v.versionId} className="kombi-admin-card">
                  <div className="kombi-admin-row-actions">
                    <span>{v.versionLabel}</span>
                    {v.isCurrent && <span className="kombi-choice">Current</span>}
                  </div>
                  <p className="learn-note">
                    Effective {fmtDate(v.effectiveDate)} · {v.changeKind} ·{' '}
                    {v.publishedAt
                      ? `published ${fmtDate(v.publishedAt)}`
                      : 'draft (not published)'}
                  </p>
                  {v.changeSummary && <p className="kombi-admin-value">{v.changeSummary}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </KombiAdminOverlay>
  );
}

/**
 * Thin route wrapper for the still-registered /admin/terms deep link. Opens the panel always-open over
 * a blank backdrop; the panel still fetches via useOverlayData, and Close returns to /admin.
 */
export function TermsOfUseAdmin() {
  const navigate = useNavigate();
  const ref = useRef<HTMLElement | null>(null);
  return <TermsOfUsePanel onClose={() => navigate('/admin')} returnFocusRef={ref} />;
}
